import * as React from 'react';

import { simplifyQueryValue } from 'js/lib/queryUtils';
import { useLocation } from 'js/lib/useRouter';

import type { Collection, Domain } from 'bundles/enterprise-admin-collection-discovery/types';

type Props = {
  preSelectedCollections?: Collection[];
  children: React.ReactNode;
};

type ContextType = {
  referrer?: string;
  selectedCollections: Collection[];
  onToggleCollection: (selected: boolean, collection: Collection) => void;
  onAddCollections: (collections: Collection[]) => void;
  onRemoveCollection: (collectionId: string) => void;
  hasSelectedCollection: (collectionId: string) => boolean;
  domain: Domain | undefined;
  onDomainChange: React.Dispatch<React.SetStateAction<Domain | undefined>>;
};

const Context = React.createContext<ContextType>({
  selectedCollections: [],
  onToggleCollection: () => {},
  hasSelectedCollection: (): boolean => false,
  onRemoveCollection: () => {},
  onAddCollections: () => {},
  domain: undefined,
  onDomainChange: () => {},
});

export const useCollectionDiscoveryContext = (): ContextType => React.useContext(Context);

export const CollectionDiscoveryContext = (props: Props): React.ReactElement => {
  const { children, preSelectedCollections } = props;
  const location = useLocation();
  const [referrer] = React.useState<string | undefined>(simplifyQueryValue(location.query.continueTo));
  const [selectedCollections, setSelectedCollections] = React.useState<Collection[]>(preSelectedCollections ?? []);
  const [domain, setDomain] = React.useState<Domain | undefined>(location.query.domain);

  return (
    <Context.Provider
      value={{
        referrer,
        selectedCollections,
        onToggleCollection: (selected: boolean, collection: Collection) => {
          setSelectedCollections((prev) => {
            if (selected) {
              return [...prev, collection];
            }
            return prev.filter((_) => _.id !== collection.id);
          });
        },
        hasSelectedCollection: (collectionId: string) => selectedCollections.some((_) => _.id === collectionId),
        onRemoveCollection: (collectionId: string) => {
          setSelectedCollections((prev) => prev.filter((_) => _.id !== collectionId));
        },
        onAddCollections: (collections: Collection[]) => {
          setSelectedCollections((prev) => Array.from(new Set([...prev, ...collections])));
        },
        domain,
        onDomainChange: setDomain,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default CollectionDiscoveryContext;
